import type { OpeningHours, Weekday } from '@ev/shared'

/** Presentation helpers for the detail panel. Pure, so they are easy to test. */

export const WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5, 6, 7]

/**
 * Metres under a kilometre, kilometres above it, one decimal at most.
 *
 * A GPS fix is good to a few metres at best, so `1,2 km` is as much precision
 * as the number can carry - `1234 m` would be false confidence.
 */
export const formatDistance = (meters: number, locale: string): string => {
  if (meters < 1000)
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(meters)} m`
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(meters / 1000)} km`
}

export const formatPower = (kilowatts: number, locale: string): string =>
  `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(kilowatts)} kW`

export const formatPrice = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    value,
  )

const invalid = (iso: string): boolean => Number.isNaN(Date.parse(iso))

export const formatDateTime = (iso: string, locale: string): string =>
  invalid(iso)
    ? iso
    : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(iso),
      )

export const formatDate = (iso: string, locale: string): string =>
  invalid(iso)
    ? iso
    : new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso))

export interface OpeningDay {
  readonly weekday: Weekday
  readonly windows: readonly string[]
}

const formatWindow = (hours: OpeningHours): string => `${hours.fromTime}-${hours.toTime}`

/** One row per weekday that has at least one window, in Monday-first order. */
export const groupOpeningHours = (hours: readonly OpeningHours[]): OpeningDay[] => {
  const byDay = new Map<Weekday, string[]>()
  hours.forEach((entry) => {
    const existing = byDay.get(entry.weekday)
    if (existing === undefined) byDay.set(entry.weekday, [formatWindow(entry)])
    else existing.push(formatWindow(entry))
  })

  const days: OpeningDay[] = []
  WEEKDAYS.forEach((weekday) => {
    const windows = byDay.get(weekday)
    if (windows !== undefined) days.push({ weekday, windows })
  })
  return days
}

const FULL_DAY = /^(00:00)-(23:59|23:59:59|24:00)$/

/** Seven days, each covered end to end - what "open 24/7" actually means. */
export const isAlwaysOpen = (hours: readonly OpeningHours[]): boolean => {
  const days = groupOpeningHours(hours)
  return days.length === 7 && days.every((day) => day.windows.some((entry) => FULL_DAY.test(entry)))
}

/**
 * A directions link rather than a map pin: the panel's job is to get somebody
 * to the charger, and `dir/?api=1` hands off to whichever navigation app the
 * device has set as its default.
 */
export const directionsUrl = (lat: number, lon: number): string =>
  `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`
